import type { EnforcedKeys } from "@suse-o11y/enforced-keys";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import type { JSON } from "@suse-o11y/json";
import { Lazy } from "@suse-o11y/lazy";
import type { PickReadonly } from "@suse-o11y/pick-readonly";
import { prop } from "@suse-o11y/prop";
import { toJSON } from "@suse-o11y/to-json";
import { z } from "zod";

import type { PersesListVariableSpec, PersesVariableTypes } from "../openapi";

import {
	type DashboardListVariableValue,
	dashboardListVariableSchema,
	listVariablePluginToPerses,
	updateListVariableValue,
} from "./_dashboard-list-variable";
import {
	type DashboardTextVariableValue,
	dashboardTextVariableSchema,
	updateTextVariableValue,
} from "./_dashboard-text-variable";

export const dashboardVariableCommonSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, "Variable name cannot be empty")
		.max(50, "Variable name is too long")
		.regex(/^[a-z_]/iu, "Variable name must start with a letter or underscore")
		.regex(/^[a-z0-9_]*$/iu, "Variable name must only contain letters, numbers, and underscores"),

	display: z
		.object({
			name: z
				.string()
				.trim()
				.transform((input) => input || undefined)
				.catch(undefined),

			description: z
				.string()
				.trim()
				.transform((input) => input || undefined)
				.catch(undefined),
		})
		.optional()
		.catch({}),
});

const variableSpecSchema = z.union([
	z.object({
		kind: z.literal("TextVariable"),
		spec: dashboardTextVariableSchema,
	}),
	z.object({
		kind: z.literal("ListVariable"),
		spec: dashboardListVariableSchema,
	}),
]);

export type DashboardVariableSpec = z.infer<typeof variableSpecSchema>;

export type DashboardVariableKind = DashboardVariableSpec["kind"];

type DashboardVariableConfig = PickReadonly<Omit<DashboardVariable, "isEqual" | "updateValue">>;

export type DashboardVariableInitConfig = EnforcedKeys<DashboardVariableConfig, "spec">;

export type DashboardVariableValue = DashboardListVariableValue | DashboardTextVariableValue;

export class DashboardVariable {
	public static schema = z
		.object({
			spec: dashboardVariableCommonSchema,
		})
		.transform(prop("spec"))
		.and(variableSpecSchema)
		.transform(
			({ name = "", display, ...spec }) => new DashboardVariable(name, display?.name, display?.description ?? "", spec),
		);

	public static fromPerses(variable: PersesVariableTypes): undefined | DashboardVariable {
		return DashboardVariable.schema.safeParse(variable).data;
	}

	public static of({
		name = "",
		label,
		description = "",
		spec,
	}: DashboardVariableInitConfig): undefined | DashboardVariable {
		const variable = new DashboardVariable(name, label, description, spec);

		// convert to/from Perses to ensure the variable is valid and normalized
		return DashboardVariable.fromPerses(variable.toPerses());
	}

	private constructor(
		public readonly name: string,
		public readonly label: undefined | string,
		public readonly description: string,
		public readonly spec: DashboardVariableSpec,
	) {}

	private readonly hash = Lazy.create(() => toJSON(this.toPerses()));

	public isEqual(another: DashboardVariable): boolean {
		return this === another || this.hash.get() === another.hash.get();
	}

	public isValueEqual(another: DashboardVariable): boolean {
		if (this.spec === another.spec) {
			return true;
		}

		if (this.spec.kind !== another.spec.kind) {
			return false;
		}

		if (this.spec.kind === "TextVariable" && another.spec.kind === "TextVariable") {
			return this.spec.spec.value === another.spec.spec.value;
		}

		if (this.spec.kind === "ListVariable" && another.spec.kind === "ListVariable") {
			return isDeepEqual(this.spec.spec.defaultValue, another.spec.spec.defaultValue);
		}

		return false;
	}

	public updateValue(value: DashboardVariableValue): DashboardVariable {
		const spec =
			this.spec.kind === "TextVariable"
				? { ...this.spec, spec: updateTextVariableValue(value, this.spec.spec) }
				: { ...this.spec, spec: updateListVariableValue(value, this.spec.spec) };

		return new DashboardVariable(this.name, this.label, this.description, spec);
	}

	public hasValue(): boolean {
		return this.spec.kind === "TextVariable" || this.spec.spec.defaultValue != null;
	}

	public hasEverythingValue(): boolean {
		return this.spec.kind === "ListVariable" && this.spec.spec.defaultValue?.type === "Everything";
	}

	public get displayValue(): undefined | string {
		const nonEmpty = (string: string) => (string === "" ? undefined : string);

		if (this.spec.kind === "TextVariable") {
			return nonEmpty(this.spec.spec.value);
		}

		const value = this.spec.spec.defaultValue;

		if (value == null) {
			return undefined;
		}

		if (value.type === "Everything") {
			return "Everything";
		}

		if (value.type === "Values") {
			// Convert the single value to an array so the same logic can be applied
			const values = value.values;

			if (values.length === 0) {
				return undefined;
			}

			if (values.length > 1) {
				return `${values.length} selected`;
			}

			if (this.spec.spec.plugin.kind === "StaticList") {
				const options = this.spec.spec.plugin.spec.values;
				const selectedOption = options.find(({ value: optionValue }) => values[0] === optionValue);
				return selectedOption?.label ?? selectedOption?.value ?? nonEmpty(values[0]);
			}

			return values[0];
		}

		return nonEmpty(value.value);
	}

	public toPerses(storeDefaultValue = true): PersesVariableTypes {
		if (this.spec.kind === "TextVariable") {
			return {
				kind: this.spec.kind,
				spec: {
					name: this.name,
					display: {
						name: this.label,
						description: this.description,
					},
					...this.spec.spec,
				},
			} satisfies JSON;
		}

		const { plugin, ...spec } = {
			...this.spec.spec,
			name: this.name,
			display: {
				name: this.label,
				description: this.description,
			},
			plugin: listVariablePluginToPerses(this.spec.spec.plugin),
			defaultValue:
				!storeDefaultValue || this.spec.spec.defaultValue == null
					? undefined
					: this.spec.spec.defaultValue.type === "Everything" || this.spec.spec.defaultValue!.type === "Value"
						? {
								kind: "singleValue",
								singleValue: toJSON(this.spec.spec.defaultValue),
							}
						: {
								kind: "sliceValues",
								sliceValues: this.spec.spec.defaultValue!.values,
							},
		} satisfies PersesListVariableSpec;

		return {
			kind: this.spec.kind,
			spec: {
				plugin,
				...(spec satisfies JSON),
			},
		};
	}
}
